import { HttpError } from '@magicdoorinc/errors';
import { createMutation, useLocalization } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { auth } from '~/config/auth';
import { createMagicDoorContext } from '~/contexts/utils';
import { normalizePhoneForBackend, validatePhone } from '~/utils/validations';
import type { AuthResultDto, CompanyOptionDto, TwoFactorChallengeDto, TwoFactorMethod, TwoFactorMethodDto } from '@magicdoorinc/auth';

export enum PasswordLoginStep {
  InputCredentials = 'InputCredentials',
  SelectMethod = 'SelectMethod',
  InputVerifyCode = 'InputVerifyCode',
}

export type PasswordLoginCredentials = {
  username: string;
  password: string;
  companyId?: string;
  redirect?: string;
};

const normalizeLoginIdentifier = (value: string): string => {
  const identifier = value.trim();
  return validatePhone(identifier) ? (normalizePhoneForBackend(identifier) ?? identifier) : identifier;
};

export const [PasswordLoginProvider, usePasswordLogin] = createMagicDoorContext('PasswordLogin', () => {
  const { t } = useLocalization();
  const [loginStep, setLoginStep] = createSignal<PasswordLoginStep>(PasswordLoginStep.InputCredentials);
  const [companies, setCompanies] = createSignal<CompanyOptionDto[] | undefined>();
  const [challenge, setChallenge] = createSignal<TwoFactorChallengeDto | undefined>();
  const [availableMethods, setAvailableMethods] = createSignal<TwoFactorMethodDto[] | undefined>();
  const [selectedMethod, setSelectedMethod] = createSignal<TwoFactorMethod | undefined>();
  const [otpRequestId, setOtpRequestId] = createSignal<string | undefined>();
  const [sentTo, setSentTo] = createSignal<TwoFactorMethodDto | undefined>();
  const [error, setError] = createSignal<string | undefined>();

  const reset = () => {
    setLoginStep(PasswordLoginStep.InputCredentials);
    setCompanies(undefined);
    setChallenge(undefined);
    setAvailableMethods(undefined);
    setSelectedMethod(undefined);
    setOtpRequestId(undefined);
    setSentTo(undefined);
  };

  const applyChallenge = (next: TwoFactorChallengeDto) => {
    setChallenge(next);
    if (next.state === 'codeSent') {
      setSelectedMethod(next.sentTo?.type);
      setOtpRequestId(next.otpRequestId);
      setSentTo(next.sentTo);
      setLoginStep(PasswordLoginStep.InputVerifyCode);
    } else {
      setSelectedMethod(undefined);
      setOtpRequestId(undefined);
      setSentTo(undefined);
      setLoginStep(PasswordLoginStep.SelectMethod);
    }
  };

  const handleSessionExpired = () => {
    reset();
    setError(t('Your session expired. Please sign in again.'));
  };

  const submitCredentials = createMutation(async (params: PasswordLoginCredentials) => {
    setError(undefined);
    try {
      const result: AuthResultDto = await auth.passwordLogin({ ...params, username: normalizeLoginIdentifier(params.username) });

      if (result.companies?.length) {
        setCompanies(result.companies);
        return;
      }
      setCompanies(undefined);

      if (result.twoFactor) {
        if (result.twoFactor.methods?.length) {
          setAvailableMethods(result.twoFactor.methods);
        } else if (result.twoFactor.sentTo) {
          setAvailableMethods([result.twoFactor.sentTo]);
        }
        applyChallenge(result.twoFactor);
        return;
      }

      if (result.interactionCode) {
        await auth.initiateOAuthFlow(result.interactionCode);
        return;
      }

      setError(t('Unknown error'));
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.getStatusCode() === 409) {
          const body = e.getJson<{ companies: CompanyOptionDto[] }>();
          if (body?.companies) {
            setCompanies(body.companies);
            return;
          }
        }
        if (e.getStatusCode() === 403) {
          setError(t('Incorrect username or password'));
          return;
        }
        if (e.getStatusCode() === 400) {
          setError(t('Username and password are required'));
          return;
        }
      }
      setError(t('Unknown error'));
    }
  });

  const sendCode = async (method: TwoFactorMethod): Promise<void> => {
    const current = challenge();
    if (!current) return;
    setError(undefined);
    setSelectedMethod(method);
    try {
      const result = await auth.sendTwoFactor({ mfaSessionToken: current.mfaSessionToken, method });
      if (result.twoFactor) {
        applyChallenge(result.twoFactor);
      }
    } catch (e) {
      if (e instanceof HttpError && e.getStatusCode() === 401) {
        handleSessionExpired();
        return;
      }
      setError(t('Unknown error'));
    }
  };

  const pickMethod = createMutation(async (method: TwoFactorMethod) => {
    await sendCode(method);
  });

  const resendCode = createMutation(async () => {
    const method = selectedMethod();
    if (method) await sendCode(method);
  });

  const changeMethod = () => {
    if ((availableMethods()?.length ?? 0) > 1) {
      setError(undefined);
      setLoginStep(PasswordLoginStep.SelectMethod);
    }
  };

  const verifyCode = createMutation(async (code: string) => {
    setError(undefined);
    const current = challenge();
    const requestId = otpRequestId();
    if (!current || !requestId) return;
    try {
      const result = await auth.verifyTwoFactor({
        mfaSessionToken: current.mfaSessionToken,
        otpRequestId: requestId,
        code,
      });
      if (result.interactionCode) {
        await auth.initiateOAuthFlow(result.interactionCode);
      }
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.getStatusCode() === 401) {
          const body = e.getJson<{ error?: string }>();
          if (body?.error === 'mfa_session_expired') {
            handleSessionExpired();
            return;
          }
          setError(t('The code you entered is incorrect'));
          return;
        }
        if (e.getStatusCode() === 429) {
          setError(t('Too many incorrect attempts, please request a new code'));
          return;
        }
      }
      setError(t('Unknown error'));
    }
  });

  const backToCredentials = () => {
    reset();
    setError(undefined);
  };

  return {
    loginStep,
    companies,
    challenge,
    availableMethods,
    selectedMethod,
    otpRequestId,
    sentTo,
    error,
    submitCredentials,
    pickMethod,
    resendCode,
    changeMethod,
    verifyCode,
    backToCredentials,
  };
});
